

























import { reactive, toRefs, computed, defineComponent, ref, Ref } from '@vue/composition-api'
import { fetchPlanVersion } from '@/api/user'

import { useToggle } from '@shared/use/use-toggle'
import Icon from '@/components/icon.vue'
import SvgIcon from '@/components/svg-icon.vue'
import clickoutside from '@/directives/clickoutside'
import i18nHandle from '@/locales'
import { UserInfoState } from '@/layout/types'
import { PrivilegeVersion } from '@/constants/enum'

import { usePlanVersion, useZhichiService, PlanVersion } from '@/components/header/use/use-zhichi-service'

interface I18nMap {
  readonly [index: string]: string
}

const I18N_MAP: I18nMap = {
  guide: i18nHandle.t('help.guide') as string,
  service: i18nHandle.t('help.service') as string,
  help: i18nHandle.t('help.help') as string,
  training: i18nHandle.t('help.training') as string,
}

interface Card {
  readonly id: string
  readonly name: string
  readonly icon: string
  readonly hoverIcon: string
  readonly size: number
  readonly className?: string
}

interface HelpProps {
  guidePath: string
  userInfoState: UserInfoState
}

const CARD_LIST: Array<Card> = [
  {
    id: 'service',
    name: I18N_MAP.service,
    icon: 'common-iconfont-service',
    hoverIcon: 'common-iconfont-service-c',
    size: 30,
    className: 'zhichiCustomBtn',
  },
  {
    id: 'help',
    name: I18N_MAP.help,
    icon: 'common-iconfont-help',
    hoverIcon: 'common-iconfont-help-c',
    size: 30,
  },
]

const useHiddenIds = (version: HelpProps['userInfoState']['privilege']['version']) => {
  const hiddenIds = computed(() => {
    // 非smart、pro、lite版本隐藏新手引导入口
    return [PrivilegeVersion.Smart, PrivilegeVersion.Pro, PrivilegeVersion.Lite].includes(version) ? [] : ['guide']
  })
  return { hiddenIds }
}

const useCardList = (
  userInfoState: HelpProps['userInfoState'],
  guidePath: HelpProps['guidePath'],
  planVersion: Ref<PlanVersion>,
) => {
  const { hiddenIds } = useHiddenIds(userInfoState.privilege.version)
  const { openZhichiService } = useZhichiService(userInfoState, planVersion)

  const realCardList = computed(() => CARD_LIST.filter(({ id }) => !hiddenIds.value.includes(id)))
  const isTwoColumnsLayout = computed(() => realCardList.value.length > 3)

  const handleCardClick = (app: Card) => {
    switch (app.id) {
      case 'service':
        openZhichiService()
        break

      case 'help':
        window.open('https://xiaomanhelp.yuque.com/qrqrfu/zx42k3/ouzigi7voauknpk3')
        break

      case 'guide':
        window.open(guidePath)
        break

      case 'training':
        window.open('https://training.xiaoman.cn/training')
        break

      default:
      // console.warn('Unhandled exception', app);
    }
  }
  return {
    realCardList,
    isTwoColumnsLayout,
    handleCardClick,
  }
}

export default defineComponent<HelpProps>({
  components: {
    Icon,
    SvgIcon,
  },

  directives: {
    clickoutside,
  },

  props: {
    userInfoState: Object,
    guidePath: {
      type: String,
      default: '/onboarding',
    },
  },

  setup(props) {
    const { planVersion } = usePlanVersion()
    const { realCardList, isTwoColumnsLayout, handleCardClick } = useCardList(
      props.userInfoState,
      props.guidePath,
      planVersion,
    )
    const [visible, toggleVisible] = useToggle()
    const [hover] = useToggle()

    return {
      realCardList,
      isTwoColumnsLayout,
      handleCardClick,
      visible,
      hidePanel: () => toggleVisible(false),
      toggleVisible,
      hover,
    }
  },
})
